import { type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingBag, User } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { ROUTES } from '@/config/constants';
import { useAuthModal } from '@/features/auth/authModalStore';
import { AvatarMenu } from '@/features/profile/components/AvatarMenu';
import { NotificationBell } from '@/features/profile/components/NotificationBell';
import { cn } from '@/lib/utils';
import { ToolkitMenu } from './ToolkitMenu';

export function TopNav({ center, sticky = true }: { center?: ReactNode; sticky?: boolean } = {}) {
  const { user, profile, loading, signOut } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const navigate = useNavigate();

  // Real avatar: the profiles row first, then the OAuth provider's metadata
  // (Discord/Google put the picture on user_metadata), else initials fallback.
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  const avatarSrc = profile?.avatar_url ?? str(meta.avatar_url) ?? str(meta.picture);
  const displayName =
    profile?.display_name ?? str(meta.full_name) ?? str(meta.name) ?? user?.email ?? null;

  const runDirectAction = (action: () => void) => {
    action();
  };

  return (
    <header className={cn('z-50 bg-transparent', sticky && 'sticky top-0')}>
      <div className="flex h-16 items-center gap-3 px-5">
        <Link to={ROUTES.home} aria-label="WINR home" className="flex-none">
          <img
            src="/winr-logo-v2.png"
            alt="WINR"
            className="h-[52px] w-auto cursor-pointer object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-px"
          />
        </Link>

        <nav className="relative z-[160] flex flex-none items-center gap-5 text-[15px] font-semibold text-white/76 md:gap-7">
          <NavLink
            to={ROUTES.home}
            end
            className={({ isActive }) =>
              cn('transition-colors hover:text-white', isActive ? 'text-white' : 'text-white/72')
            }
          >
            Explore
          </NavLink>
          <NavLink
            to={ROUTES.myCommunities}
            className={({ isActive }) =>
              cn('transition-colors hover:text-white', isActive ? 'text-white' : 'text-white/72')
            }
          >
            Community
          </NavLink>
          <NavLink
            to={ROUTES.inbox}
            className={({ isActive }) =>
              cn('transition-colors hover:text-white', isActive ? 'text-white' : 'text-white/72')
            }
          >
            Inbox
          </NavLink>
          <button
            type="button"
            onClick={() => runDirectAction(() => (user ? navigate(ROUTES.profileSelf) : openAuth()))}
            className="text-white/72 transition-colors hover:text-white"
          >
            Profile
          </button>
          <NavLink
            to={ROUTES.marketplace}
            className={({ isActive }) =>
              cn(
                'inline-flex items-center gap-1.5 transition-colors hover:text-white',
                isActive ? 'text-white' : 'text-white/72',
              )
            }
          >
            <ShoppingBag size={15} strokeWidth={2} />
            Marketplace
          </NavLink>
        </nav>

        <div className="min-w-0 flex-1">{center}</div>

        <div className="flex flex-none items-center gap-2.5">
          <NotificationBell />

          <ToolkitMenu />

          {!loading &&
            (user ? (
              <AvatarMenu
                avatarSrc={avatarSrc}
                displayName={displayName}
                email={user.email}
                onViewProfile={() => navigate(ROUTES.profileSelf)}
                onSettings={() => navigate(ROUTES.settings)}
                onLogout={() => void signOut()}
              />
            ) : (
              <button
                onClick={openAuth}
                aria-label="Sign in"
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white/15 text-hi transition-colors hover:border-white/35"
              >
                <User size={18} strokeWidth={2} />
              </button>
            ))}
        </div>
      </div>
    </header>
  );
}
